import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  DatePicker,
  Form,
  Checkbox,
  InputNumber,
  Row,
  Col,
  Alert,
  Typography,
  Radio,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PermissionButton from '../components/Auth/PermissionButton';
import { User, hasPermission } from '../utils/permissions';

const { RangePicker } = DatePicker;
const { Option } = Select;
const PAYOUT_CHANNEL_OPTIONS = ['渠道A（极速代付）', '渠道B（三方代付）', '渠道C（网银出款）'];

// 提现状态枚举
enum WithdrawalStatus {
  PENDING_AUDIT = '待审核',
  PENDING_PAYOUT = '待出款',
  PAYOUT_PROCESSING = '出款中',
  REJECTED = '已驳回',
  PAID = '已出款',
}

type PayoutStatus = '代付中' | '代付成功' | '代付失败';

// 标注原因类型
type AuditReason = '打码异常' | '新用户' | '大额' | '连续提现' | '长胜' | '异常标签' | '';

// 自动审核配置接口
interface AutoAuditConfig {
  checkWagering: boolean;
  checkNewUser: boolean;
  checkLargeAmount: boolean;
  largeAmountThreshold?: number;
  checkWithdrawalCount: boolean;
  withdrawalCountThreshold?: number;
  checkDepositWithdrawalRatio: boolean;
  depositWithdrawalRatioThreshold?: number;
  checkAbnormalTag: boolean;
  abnormalTagIds?: string;
}

// 提现申请数据接口
interface RelatedAccount {
  uid: string;
  diff: number;
}

interface WithdrawalRecord {
  id: number;
  orderNo: string;
  userId: string;
  uid: string;
  username: string;
  vipLevel: string;
  memberAccount: string;
  name: string;
  withdrawalAccount: string;
  bankName: string;
  withdrawalAmount: number;
  accumulatedDeposit: number;
  accumulatedWithdrawal: number;
  paymentAmount: number;
  status: WithdrawalStatus;
  payoutStatus?: PayoutStatus;
  payoutChannel?: string;
  remark?: string;
  auditReason?: AuditReason;
  rejectReason?: string;
  registerTime: string;
  applyTime: string;
  auditTime?: string;
  completeTime?: string;
  // 用于自动审核检测的字段
  wageringRequirement?: number;
  isFirstWithdrawal?: boolean;
  withdrawalCountSinceLastDeposit?: number;
  depositWithdrawalRatio?: number;
  userTags?: string[];
  // 风控与财务详情
  ltv: {
    totalDeposit: number;
    totalWithdrawal: number;
    net: number;
    totalWinLoss: number;
  };
  welfare: {
    totalBonus: number;
    totalRebate: number;
    totalWagering: number;
    wageringMultiple: number;
    currentWagering: number;
    currentBonus: number;
    currentWageringMultiple: number;
  };
  singleDeposit: {
    lastDepositAmount: number;
    lastDepositTime: string;
    withdrawCountSinceLastDeposit: number;
    winLossSinceLastDeposit: number;
  };
  riskLinks: {
    sameDeviceCount: number;
    sameIpCount: number;
    sameDeviceAccounts: RelatedAccount[];
    sameIpAccounts: RelatedAccount[];
  };
  payoutSimulation?: 'success' | 'fail';
}

interface WithdrawalRiskControlProps {
  user?: User | null;
}

const WithdrawalRiskControl: React.FC<WithdrawalRiskControlProps> = ({ user }) => {
  // 从 localStorage 获取用户（如果没有通过 props 传入）
  const getCurrentUser = (): User | null => {
    if (user) return user;
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  };
  
  const currentUser = getCurrentUser();
  const [loading, setLoading] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<WithdrawalRecord | null>(null);
  const [relatedModalVisible, setRelatedModalVisible] = useState(false);
  const [relatedModalTitle, setRelatedModalTitle] = useState('关联账号');
  const [relatedAccounts, setRelatedAccounts] = useState<RelatedAccount[]>([]);
  const [payoutModalVisible, setPayoutModalVisible] = useState(false);
  const [payoutRecord, setPayoutRecord] = useState<WithdrawalRecord | null>(null);
  const [selectedChannel, setSelectedChannel] = useState('渠道A（极速代付）');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchPayoutModalVisible, setBatchPayoutModalVisible] = useState(false);
  const [batchChannel, setBatchChannel] = useState('渠道A（极速代付）');
  const [autoPayoutEnabled, setAutoPayoutEnabled] = useState(false);
  const [autoPayoutModalVisible, setAutoPayoutModalVisible] = useState(false);
  const [autoPayoutChannels, setAutoPayoutChannels] = useState<string[]>([]);
  const [autoPayoutRule, setAutoPayoutRule] = useState<'轮询' | '随机'>('轮询');
  const [autoPayoutMaxAmount, setAutoPayoutMaxAmount] = useState<number | undefined>(undefined);
  const autoPayoutCursorRef = useRef(0);
  
  // 筛选条件
  const [orderNo, setOrderNo] = useState('');
  const [userId, setUserId] = useState('');
  const [memberAccount, setMemberAccount] = useState('');
  const [auditStatus, setAuditStatus] = useState('全部');
  const [payoutStatus, setPayoutStatus] = useState('全部');
  const [payoutChannel, setPayoutChannel] = useState('全部');
  const [timeFilterType, setTimeFilterType] = useState<'apply' | 'audit' | 'complete'>('apply');
  const [timeRange, setTimeRange] = useState<any>([
    dayjs('2026-01-31'),
    dayjs('2026-01-31'),
  ]);
  
  // 自动审核配置和状态
  const [autoAuditEnabled, setAutoAuditEnabled] = useState(false);
  const [autoAuditConfig, setAutoAuditConfig] = useState<AutoAuditConfig>({
    checkWagering: false,
    checkNewUser: false,
    checkLargeAmount: false,
    largeAmountThreshold: 10000,
    checkWithdrawalCount: false,
    withdrawalCountThreshold: 5,
    checkDepositWithdrawalRatio: false,
    depositWithdrawalRatioThreshold: 1.5,
    checkAbnormalTag: false,
    abnormalTagIds: '',
  });

  // 模拟提现数据
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalRecord[]>([]);

  // 从本地存储加载配置
  useEffect(() => {
    const savedConfig = localStorage.getItem('autoAuditConfig');
    const savedEnabled = localStorage.getItem('autoAuditEnabled');
    if (savedConfig) {
      const config = JSON.parse(savedConfig);
      setAutoAuditConfig(config);
      form.setFieldsValue(config);
    }
    if (savedEnabled) {
      setAutoAuditEnabled(savedEnabled === 'true');
    }

    const savedAutoPayout = localStorage.getItem('autoPayoutConfig');
    if (savedAutoPayout) {
      const config = JSON.parse(savedAutoPayout);
      setAutoPayoutEnabled(config.enabled === true);
      setAutoPayoutChannels(config.channels || []);
      setAutoPayoutRule(config.rule || '轮询');
      setAutoPayoutMaxAmount(config.maxAmount);
    }
    
    // 加载模拟数据
    loadMockData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当自动审核开启时，自动审核待审核的记录
  useEffect(() => {
    if (autoAuditEnabled && withdrawalData.length > 0) {
      const pendingRecords = withdrawalData.filter(r => r.status === WithdrawalStatus.PENDING_AUDIT);
      if (pendingRecords.length > 0) {
        // 使用函数式更新，避免依赖问题
        autoAuditPendingRecords(withdrawalData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAuditEnabled]);

  useEffect(() => {
    if (!autoPayoutEnabled) return;
    if (!autoPayoutMaxAmount || autoPayoutChannels.length === 0) return;
    const eligible = withdrawalData.filter(
      item =>
        item.status === WithdrawalStatus.PENDING_PAYOUT &&
        item.withdrawalAmount <= autoPayoutMaxAmount
    );
    if (!eligible.length) return;
    batchPayout(eligible, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPayoutEnabled, autoPayoutMaxAmount, autoPayoutChannels]);

  // 加载模拟数据
  const loadMockData = () => {
    // 模拟数据
    const mockData: WithdrawalRecord[] = [
      {
        id: 1,
        orderNo: 'WD202601160001',
        userId: 'user_001',
        uid: '882910',
        username: 'shenghe001',
        vipLevel: 'VIP1',
        memberAccount: 'shenghe001',
        name: '张三',
        withdrawalAccount: '6222021234567890123',
        bankName: 'Vietcombank',
        withdrawalAmount: 5000000,
        accumulatedDeposit: 150000000,
        accumulatedWithdrawal: 50000000,
        paymentAmount: 5000000,
        status: WithdrawalStatus.PENDING_AUDIT,
        registerTime: '2025-12-01 10:00:00',
        applyTime: '2026-01-31 09:00:00',
        wageringRequirement: 0,
        isFirstWithdrawal: false,
        withdrawalCountSinceLastDeposit: 2,
        depositWithdrawalRatio: 0.5,
        userTags: [],
        ltv: {
          totalDeposit: 150000000,
          totalWithdrawal: 50000000,
          net: 100000000,
          totalWinLoss: -120000000,
        },
        welfare: {
          totalBonus: 8000000,
          totalRebate: 1500000,
          totalWagering: 1200000000,
          wageringMultiple: 7.5,
          currentWagering: 35000000,
          currentBonus: 1200000,
          currentWageringMultiple: 6.8,
        },
        singleDeposit: {
          lastDepositAmount: 2000000,
          lastDepositTime: '2026-01-14 22:40:00',
          withdrawCountSinceLastDeposit: 1,
          winLossSinceLastDeposit: -450000,
        },
        riskLinks: {
          sameDeviceCount: 1,
          sameIpCount: 2,
          sameDeviceAccounts: [{ uid: '883001', diff: 1200000 }],
          sameIpAccounts: [
            { uid: '883100', diff: 520000 },
            { uid: '883200', diff: -310000 },
          ],
        },
      },
      {
        id: 2,
        orderNo: 'WD202601160002',
        userId: 'user_002',
        uid: '882912',
        username: 'hongzhongtest001',
        vipLevel: 'VIP3',
        memberAccount: 'hongzhongtest001',
        name: '李四',
        withdrawalAccount: '6222021234567890456',
        bankName: 'Techcombank',
        withdrawalAmount: 45000000,
        accumulatedDeposit: 800000000,
        accumulatedWithdrawal: 150000000,
        paymentAmount: 45000000,
        status: WithdrawalStatus.PENDING_PAYOUT,
        payoutStatus: '代付中',
        payoutChannel: '渠道A（极速代付）',
        auditTime: '2026-01-31 09:22:10',
        registerTime: '2026-01-10 10:00:00',
        applyTime: '2026-01-31 09:15:00',
        wageringRequirement: 1000,
        isFirstWithdrawal: true,
        withdrawalCountSinceLastDeposit: 1,
        depositWithdrawalRatio: 3.0,
        userTags: [],
        ltv: {
          totalDeposit: 800000000,
          totalWithdrawal: 150000000,
          net: 650000000,
          totalWinLoss: -500000000,
        },
        welfare: {
          totalBonus: 20000000,
          totalRebate: 5000000,
          totalWagering: 3500000000,
          wageringMultiple: 4.1,
          currentWagering: 120000000,
          currentBonus: 3000000,
          currentWageringMultiple: 5.2,
        },
        singleDeposit: {
          lastDepositAmount: 150000000,
          lastDepositTime: '2026-01-15 19:20:00',
          withdrawCountSinceLastDeposit: 0,
          winLossSinceLastDeposit: -12000000,
        },
        riskLinks: {
          sameDeviceCount: 2,
          sameIpCount: 3,
          sameDeviceAccounts: [
            { uid: '882913', diff: 1500000 },
            { uid: '882914', diff: -200000 },
          ],
          sameIpAccounts: [
            { uid: '882920', diff: 900000 },
            { uid: '882930', diff: 1200000 },
            { uid: '882940', diff: -650000 },
          ],
        },
        payoutSimulation: 'success',
      },
      {
        id: 3,
        orderNo: 'WD202601160003',
        userId: 'user_003',
        uid: '882915',
        username: 'vn_king_99',
        vipLevel: 'VIP5',
        memberAccount: 'vn_king_99',
        name: '王五',
        withdrawalAccount: '6222021234567890999',
        bankName: 'Vietinbank',
        withdrawalAmount: 50000000,
        accumulatedDeposit: 800000000,
        accumulatedWithdrawal: 200000000,
        paymentAmount: 50000000,
        status: WithdrawalStatus.PAYOUT_PROCESSING,
        payoutStatus: '代付中',
        payoutChannel: '渠道A（极速代付）',
        auditTime: '2026-01-31 08:40:10',
        registerTime: '2025-11-11 12:12:12',
        applyTime: '2026-01-31 08:20:00',
        wageringRequirement: 0,
        isFirstWithdrawal: false,
        withdrawalCountSinceLastDeposit: 2,
        depositWithdrawalRatio: 0.8,
        userTags: [],
        ltv: {
          totalDeposit: 800000000,
          totalWithdrawal: 200000000,
          net: 600000000,
          totalWinLoss: -650000000,
        },
        welfare: {
          totalBonus: 35000000,
          totalRebate: 9000000,
          totalWagering: 6000000000,
          wageringMultiple: 6.2,
          currentWagering: 180000000,
          currentBonus: 2000000,
          currentWageringMultiple: 3.6,
        },
        singleDeposit: {
          lastDepositAmount: 10000000,
          lastDepositTime: '2026-01-15 21:05:00',
          withdrawCountSinceLastDeposit: 1,
          winLossSinceLastDeposit: -3200000,
        },
        riskLinks: {
          sameDeviceCount: 1,
          sameIpCount: 2,
          sameDeviceAccounts: [{ uid: '882916', diff: 800000 }],
          sameIpAccounts: [
            { uid: '882917', diff: 650000 },
            { uid: '882918', diff: -120000 },
          ],
        },
        payoutSimulation: 'success',
      },
      {
        id: 4,
        orderNo: 'WD202601160004',
        userId: 'user_004',
        uid: '882919',
        username: 'vip_gold_1',
        vipLevel: 'VIP7',
        memberAccount: 'vip_gold_1',
        name: '赵六',
        withdrawalAccount: '6222021234567890111',
        bankName: 'Agribank',
        withdrawalAmount: 20000000,
        accumulatedDeposit: 50000000,
        accumulatedWithdrawal: 10000000,
        paymentAmount: 20000000,
        status: WithdrawalStatus.PAID,
        payoutStatus: '代付成功',
        payoutChannel: '渠道C（网银出款）',
        auditTime: '2026-01-31 07:30:10',
        completeTime: '2026-01-31 07:45:30',
        registerTime: '2025-10-20 09:30:00',
        applyTime: '2026-01-31 07:20:00',
        wageringRequirement: 0,
        isFirstWithdrawal: false,
        withdrawalCountSinceLastDeposit: 3,
        depositWithdrawalRatio: 0.6,
        userTags: [],
        ltv: {
          totalDeposit: 50000000,
          totalWithdrawal: 10000000,
          net: 40000000,
          totalWinLoss: -38000000,
        },
        welfare: {
          totalBonus: 2000000,
          totalRebate: 800000,
          totalWagering: 260000000,
          wageringMultiple: 4.7,
          currentWagering: 8000000,
          currentBonus: 500000,
          currentWageringMultiple: 2.9,
        },
        singleDeposit: {
          lastDepositAmount: 5000000,
          lastDepositTime: '2026-01-14 18:10:00',
          withdrawCountSinceLastDeposit: 0,
          winLossSinceLastDeposit: -600000,
        },
        riskLinks: {
          sameDeviceCount: 0,
          sameIpCount: 1,
          sameDeviceAccounts: [],
          sameIpAccounts: [{ uid: '882925', diff: 300000 }],
        },
      },
      {
        id: 5,
        orderNo: 'WD202601160005',
        userId: 'user_005',
        uid: '882921',
        username: 'risk_case_88',
        vipLevel: 'VIP2',
        memberAccount: 'risk_case_88',
        name: '孙七',
        withdrawalAccount: '6222021234567890777',
        bankName: 'ACB',
        withdrawalAmount: 8000000,
        accumulatedDeposit: 10000000,
        accumulatedWithdrawal: 9000000,
        paymentAmount: 8000000,
        status: WithdrawalStatus.REJECTED,
        auditTime: '2026-01-31 10:12:30',
        rejectReason: '疑似套利',
        registerTime: '2026-01-14 14:00:00',
        applyTime: '2026-01-31 09:40:00',
        wageringRequirement: 2000,
        isFirstWithdrawal: true,
        withdrawalCountSinceLastDeposit: 1,
        depositWithdrawalRatio: 1.9,
        userTags: ['异常标签'],
        ltv: {
          totalDeposit: 10000000,
          totalWithdrawal: 9000000,
          net: 1000000,
          totalWinLoss: 2000000,
        },
        welfare: {
          totalBonus: 1500000,
          totalRebate: 200000,
          totalWagering: 15000000,
          wageringMultiple: 1.3,
          currentWagering: 5000000,
          currentBonus: 800000,
          currentWageringMultiple: 0.9,
        },
        singleDeposit: {
          lastDepositAmount: 3000000,
          lastDepositTime: '2026-01-15 09:10:00',
          withdrawCountSinceLastDeposit: 1,
          winLossSinceLastDeposit: 1800000,
        },
        riskLinks: {
          sameDeviceCount: 2,
          sameIpCount: 4,
          sameDeviceAccounts: [
            { uid: '882922', diff: 200000 },
            { uid: '882923', diff: 150000 },
          ],
          sameIpAccounts: [
            { uid: '882924', diff: 300000 },
            { uid: '882926', diff: 450000 },
            { uid: '882927', diff: -120000 },
            { uid: '882928', diff: 90000 },
          ],
        },
      },
      {
        id: 6,
        orderNo: 'WD202601160006',
        userId: 'user_006',
        uid: '882930',
        username: 'pay_fail_01',
        vipLevel: 'VIP3',
        memberAccount: 'pay_fail_01',
        name: '周八',
        withdrawalAccount: '6222021234567890888',
        bankName: 'Vietcombank',
        withdrawalAmount: 120000000,
        accumulatedDeposit: 300000000,
        accumulatedWithdrawal: 80000000,
        paymentAmount: 120000000,
        status: WithdrawalStatus.PAYOUT_PROCESSING,
        payoutStatus: '代付失败',
        payoutChannel: '渠道B（三方代付）',
        auditTime: '2026-01-31 09:05:00',
        completeTime: '2026-01-31 09:18:00',
        registerTime: '2025-12-20 16:00:00',
        applyTime: '2026-01-31 08:55:00',
        wageringRequirement: 0,
        isFirstWithdrawal: false,
        withdrawalCountSinceLastDeposit: 2,
        depositWithdrawalRatio: 0.7,
        userTags: [],
        ltv: {
          totalDeposit: 300000000,
          totalWithdrawal: 80000000,
          net: 220000000,
          totalWinLoss: -250000000,
        },
        welfare: {
          totalBonus: 12000000,
          totalRebate: 3000000,
          totalWagering: 1400000000,
          wageringMultiple: 4.3,
          currentWagering: 60000000,
          currentBonus: 1500000,
          currentWageringMultiple: 3.2,
        },
        singleDeposit: {
          lastDepositAmount: 20000000,
          lastDepositTime: '2026-01-15 20:00:00',
          withdrawCountSinceLastDeposit: 0,
          winLossSinceLastDeposit: -3000000,
        },
        riskLinks: {
          sameDeviceCount: 1,
          sameIpCount: 1,
          sameDeviceAccounts: [{ uid: '882931', diff: 900000 }],
          sameIpAccounts: [{ uid: '882932', diff: -400000 }],
        },
        payoutSimulation: 'fail',
      },
    ];
    setWithdrawalData(mockData);
  };

  // 自动审核所有待处理的记录
  const autoAuditPendingRecords = (records?: WithdrawalRecord[]) => {
    const currentRecords = records || withdrawalData;
    const pendingRecords = currentRecords.filter(r => r.status === WithdrawalStatus.PENDING_AUDIT);
    if (pendingRecords.length === 0) return;

    setLoading(true);

    // 延迟执行审核，模拟系统审核过程
    setTimeout(() => {
      const updatedRecords = currentRecords.map(record => {
        if (record.status === WithdrawalStatus.PENDING_AUDIT) {
          const auditResult = performAutoAudit(record);
          
          // 记录日志
          logAuditAction(record, auditResult);
          
          return {
            ...record,
            status: auditResult.status,
            auditReason: auditResult.reason,
            auditTime: auditResult.status === WithdrawalStatus.PENDING_PAYOUT
              ? dayjs().format('YYYY-MM-DD HH:mm:ss')
              : record.auditTime,
          };
        }
        return record;
      });

      setWithdrawalData(updatedRecords);
      setLoading(false);
      if (pendingRecords.length > 0) {
        message.success(`已自动审核 ${pendingRecords.length} 条提现申请`);
      }
    }, 800);
  };

  // 记录审核日志
  const logAuditAction = (
    record: WithdrawalRecord,
    auditResult: { status: WithdrawalStatus; reason?: AuditReason }
  ) => {
    const logData = {
      timestamp: new Date().toISOString(),
      orderNo: record.orderNo,
      userId: record.userId,
      username: record.username,
      withdrawalAmount: record.withdrawalAmount,
      auditType: '自动审核',
      originalStatus: record.status,
      newStatus: auditResult.status,
      auditReason: auditResult.reason || '无',
      config: autoAuditConfig,
    };
    
    console.log('[自动审核日志]', logData);
    
    // 保存到本地存储（实际项目中应该发送到后端）
    const logs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
    logs.push(logData);
    // 只保留最近1000条日志
    if (logs.length > 1000) {
      logs.splice(0, logs.length - 1000);
    }
    localStorage.setItem('auditLogs', JSON.stringify(logs));
  };

  // 处理搜索
  const handleSearch = () => {
    setLoading(true);
    // 模拟搜索 - 在实际项目中这里会调用API
    setTimeout(() => {
      setLoading(false);
      
      // 如果自动审核已开启，自动审核新加载的待处理记录
      if (autoAuditEnabled) {
        const pendingRecords = withdrawalData.filter(r => r.status === WithdrawalStatus.PENDING_AUDIT);
        if (pendingRecords.length > 0) {
          autoAuditPendingRecords();
        }
      }
      
      message.success('搜索完成');
    }, 500);
  };

  // 处理重置
  const handleReset = () => {
    setOrderNo('');
    setUserId('');
    setMemberAccount('');
    setAuditStatus('全部');
    setPayoutStatus('全部');
    setPayoutChannel('全部');
    setTimeFilterType('apply');
    setTimeRange([dayjs('2026-01-31'), dayjs('2026-01-31')]);
    handleSearch();
  };

  // 打开配置弹窗
  const handleOpenConfig = () => {
    form.setFieldsValue(autoAuditConfig);
    setConfigModalVisible(true);
  };

  // 关闭配置弹窗
  const handleCloseConfig = () => {
    setConfigModalVisible(false);
    form.resetFields();
  };

  // 保存自动审核配置
  const handleSaveConfig = async () => {
    try {
      const values = await form.validateFields();
      const newConfig: AutoAuditConfig = {
        checkWagering: values.checkWagering || false,
        checkNewUser: values.checkNewUser || false,
        checkLargeAmount: values.checkLargeAmount || false,
        largeAmountThreshold: values.checkLargeAmount ? values.largeAmountThreshold : undefined,
        checkWithdrawalCount: values.checkWithdrawalCount || false,
        withdrawalCountThreshold: values.checkWithdrawalCount ? values.withdrawalCountThreshold : undefined,
        checkDepositWithdrawalRatio: values.checkDepositWithdrawalRatio || false,
        depositWithdrawalRatioThreshold: values.checkDepositWithdrawalRatio ? values.depositWithdrawalRatioThreshold : undefined,
        checkAbnormalTag: values.checkAbnormalTag || false,
        abnormalTagIds: values.checkAbnormalTag ? values.abnormalTagIds : undefined,
      };
      
      setAutoAuditConfig(newConfig);
      localStorage.setItem('autoAuditConfig', JSON.stringify(newConfig));
      localStorage.setItem('autoAuditEnabled', 'true');
      setAutoAuditEnabled(true);
      setConfigModalVisible(false);
      message.success('自动审核配置已保存并启用');
    } catch (error) {
      console.error('配置验证失败:', error);
    }
  };

  // 关闭自动审核
  const handleDisableAutoAudit = () => {
    setAutoAuditEnabled(false);
    localStorage.setItem('autoAuditEnabled', 'false');
    message.success('自动审核已关闭');
  };

  // 自动审核逻辑
  const performAutoAudit = (record: WithdrawalRecord): { status: WithdrawalStatus; reason?: AuditReason } => {
    // 如果自动审核未开启，返回原状态
    if (!autoAuditEnabled) {
      return { status: record.status };
    }

    // 1. 检测打码量
    if (autoAuditConfig.checkWagering) {
      if (record.wageringRequirement !== undefined && record.wageringRequirement > 0) {
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 打码异常 (打码需求: ${record.wageringRequirement})`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '打码异常' };
      }
    }

    // 2. 检测新用户
    if (autoAuditConfig.checkNewUser) {
      if (record.isFirstWithdrawal) {
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 新用户`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '新用户' };
      }
    }

    // 3. 检测大额提现
    if (autoAuditConfig.checkLargeAmount && autoAuditConfig.largeAmountThreshold !== undefined) {
      if (record.withdrawalAmount > autoAuditConfig.largeAmountThreshold) {
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 大额 (提现金额: ${record.withdrawalAmount}, 阈值: ${autoAuditConfig.largeAmountThreshold})`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '大额' };
      }
    }

    // 4. 检测提现次数
    if (autoAuditConfig.checkWithdrawalCount && autoAuditConfig.withdrawalCountThreshold !== undefined) {
      if (record.withdrawalCountSinceLastDeposit !== undefined && 
          record.withdrawalCountSinceLastDeposit > autoAuditConfig.withdrawalCountThreshold) {
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 连续提现 (提现次数: ${record.withdrawalCountSinceLastDeposit}, 阈值: ${autoAuditConfig.withdrawalCountThreshold})`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '连续提现' };
      }
    }

    // 5. 检测提充比
    if (autoAuditConfig.checkDepositWithdrawalRatio && autoAuditConfig.depositWithdrawalRatioThreshold !== undefined) {
      const ratio = record.accumulatedDeposit > 0 
        ? record.withdrawalAmount / record.accumulatedDeposit 
        : 0;
      if (ratio > autoAuditConfig.depositWithdrawalRatioThreshold) {
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 长胜 (提充比: ${ratio.toFixed(2)}, 阈值: ${autoAuditConfig.depositWithdrawalRatioThreshold})`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '长胜' };
      }
    }

    // 6. 检测异常标签
    if (autoAuditConfig.checkAbnormalTag && autoAuditConfig.abnormalTagIds) {
      const tagIds = autoAuditConfig.abnormalTagIds.split(',').map(t => t.trim()).filter(t => t);
      if (record.userTags && record.userTags.some(tag => tagIds.includes(tag))) {
        const matchedTags = record.userTags.filter(tag => tagIds.includes(tag));
        console.log(`[自动审核] 订单 ${record.orderNo} 检测不通过: 异常标签 (匹配标签: ${matchedTags.join(', ')})`);
        return { status: WithdrawalStatus.PENDING_AUDIT, reason: '异常标签' };
      }
    }

    // 所有检测通过
    console.log(`[自动审核] 订单 ${record.orderNo} 所有检测通过，自动通过审核`);
    return { status: WithdrawalStatus.PENDING_PAYOUT };
  };

  const openAuditModal = (record: WithdrawalRecord) => {
    setSelectedRecord(record);
    setAuditModalVisible(true);
  };

  const handleApprove = () => {
    if (!selectedRecord) return;
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const updatedData = withdrawalData.map(item =>
      item.id === selectedRecord.id
        ? {
            ...item,
            status: WithdrawalStatus.PENDING_PAYOUT,
            auditTime: now,
            auditReason: undefined,
          }
        : item
    );
    setWithdrawalData(updatedData);
    setAuditModalVisible(false);
    message.success('审核通过，待出款');
    console.log(`[审核日志] 订单 ${selectedRecord.orderNo} 人工审核通过`, {
      timestamp: new Date().toISOString(),
      operator: '当前用户',
    });
  };

  const openRelatedModal = (title: string, list: RelatedAccount[]) => {
    setRelatedModalTitle(title);
    setRelatedAccounts(list);
    setRelatedModalVisible(true);
  };

  const openPayoutModal = (record: WithdrawalRecord) => {
    setPayoutRecord(record);
    setSelectedChannel(record.payoutChannel || '渠道A（极速代付）');
    setPayoutModalVisible(true);
  };

  const handleConfirmPayout = () => {
    if (!payoutRecord) return;
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const nextData = withdrawalData.map(item =>
      item.id === payoutRecord.id
        ? {
            ...item,
            status: WithdrawalStatus.PAYOUT_PROCESSING,
            payoutStatus: '代付中' as PayoutStatus,
            payoutChannel: selectedChannel,
            auditTime: item.auditTime || now,
          }
        : item
    );
    setWithdrawalData(nextData);
    setPayoutModalVisible(false);
    message.success('已发起出款，下发中');

    const simulation = payoutRecord.payoutSimulation || 'success';
    setTimeout(() => {
      setWithdrawalData(prev =>
        prev.map(item => {
          if (item.id !== payoutRecord.id) return item;
          if (simulation === 'fail') {
            return {
              ...item,
              status: WithdrawalStatus.PAYOUT_PROCESSING,
              payoutStatus: '代付失败',
              completeTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };
          }
          return {
            ...item,
            status: WithdrawalStatus.PAID,
            payoutStatus: '代付成功',
            completeTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          };
        })
      );
    }, 1200);
  };

  const formatCurrency = (value: number) => value.toLocaleString();

  const pickAutoChannel = () => {
    if (autoPayoutChannels.length === 0) return '渠道A（极速代付）';
    if (autoPayoutChannels.length >= 2 && autoPayoutRule === '随机') {
      const idx = Math.floor(Math.random() * autoPayoutChannels.length);
      return autoPayoutChannels[idx];
    }
    const idx = autoPayoutCursorRef.current % autoPayoutChannels.length;
    autoPayoutCursorRef.current += 1;
    return autoPayoutChannels[idx];
  };

  const batchPayout = (records: WithdrawalRecord[], isAuto = false) => {
    if (!records.length) return;
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const mapped = records.map((record) => ({
      ...record,
      status: WithdrawalStatus.PAYOUT_PROCESSING,
      payoutStatus: '代付中' as PayoutStatus,
      payoutChannel: isAuto ? pickAutoChannel() : batchChannel,
      auditTime: record.auditTime || now,
    }));
    setWithdrawalData(prev =>
      prev.map(item => {
        const match = mapped.find(r => r.id === item.id);
        return match ? match : item;
      })
    );

    mapped.forEach(record => {
      const simulation = record.payoutSimulation || 'success';
      setTimeout(() => {
        setWithdrawalData(prev =>
          prev.map(item => {
            if (item.id !== record.id) return item;
            if (simulation === 'fail') {
              return {
                ...item,
                status: WithdrawalStatus.PAYOUT_PROCESSING,
                payoutStatus: '代付失败',
                completeTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              };
            }
            return {
              ...item,
              status: WithdrawalStatus.PAID,
              payoutStatus: '代付成功',
              completeTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };
          })
        );
      }, 1200);
    });
  };

  const handleBatchPayout = () => {
    const selectedRecords = withdrawalData.filter(
      item => selectedRowKeys.includes(item.id) && item.status === WithdrawalStatus.PENDING_PAYOUT
    );
    if (!selectedRecords.length) {
      message.warning('请选择待出款的订单');
      return;
    }
    batchPayout(selectedRecords);
    setBatchPayoutModalVisible(false);
    setSelectedRowKeys([]);
    message.success(`已发起 ${selectedRecords.length} 笔批量出款`);
  };

  const openAutoPayoutModal = () => {
    setAutoPayoutModalVisible(true);
  };

  const handleSaveAutoPayout = () => {
    if (!autoPayoutMaxAmount) {
      message.warning('请填写自动下款最大金额');
      return;
    }
    if (autoPayoutChannels.length === 0) {
      message.warning('请选择自动下款通道');
      return;
    }
    if (autoPayoutChannels.length >= 2 && !autoPayoutRule) {
      message.warning('请选择自动下款规则');
      return;
    }
    setAutoPayoutEnabled(true);
    localStorage.setItem(
      'autoPayoutConfig',
      JSON.stringify({
        enabled: true,
        channels: autoPayoutChannels,
        rule: autoPayoutChannels.length >= 2 ? autoPayoutRule : '轮询',
        maxAmount: autoPayoutMaxAmount,
      })
    );
    setAutoPayoutModalVisible(false);
    message.success('自动下款已开启');
  };

  const handleDisableAutoPayout = () => {
    setAutoPayoutEnabled(false);
    localStorage.setItem(
      'autoPayoutConfig',
      JSON.stringify({
        enabled: false,
        channels: autoPayoutChannels,
        rule: autoPayoutRule,
        maxAmount: autoPayoutMaxAmount,
      })
    );
    message.success('自动下款已关闭');
  };

  // 处理驳回
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectingRecord, setRejectingRecord] = useState<WithdrawalRecord | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleConfirmReject = () => {
    if (!rejectingRecord) return;
    
    const reason = rejectReason.trim() || '未填写原因';
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    
    // 更新状态
    const updatedData = withdrawalData.map(item =>
      item.id === rejectingRecord.id
        ? { ...item, status: WithdrawalStatus.REJECTED, rejectReason: reason, auditTime: item.auditTime || now }
        : item
    );
    setWithdrawalData(updatedData);
    setRejectModalVisible(false);
    setRejectingRecord(null);
    setRejectReason('');
    setAuditModalVisible(false);
    message.success('已驳回');
    
    // 记录日志
    console.log(`[审核日志] 订单 ${rejectingRecord.orderNo} 人工审核驳回`, {
      timestamp: new Date().toISOString(),
      operator: '当前用户',
      reason: reason,
    });
  };

  // 获取状态标签颜色
  const getStatusColor = (status: WithdrawalStatus) => {
    const colors: Record<WithdrawalStatus, string> = {
      [WithdrawalStatus.PENDING_AUDIT]: 'warning',
      [WithdrawalStatus.PENDING_PAYOUT]: 'processing',
      [WithdrawalStatus.PAYOUT_PROCESSING]: 'processing',
      [WithdrawalStatus.REJECTED]: 'error',
      [WithdrawalStatus.PAID]: 'success',
    };
    return colors[status] || 'default';
  };

  // 表格列定义
  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
      fixed: 'left' as const,
    },
    {
      title: '用户信息',
      key: 'userInfo',
      width: 190,
      render: (_: any, record: WithdrawalRecord) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{record.username}</span>
            <Tag color="gold">{record.vipLevel}</Tag>
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>UID：{record.uid}</div>
        </div>
      ),
    },
    {
      title: '申请金额/净额',
      key: 'amounts',
      width: 140,
      render: (_: any, record: WithdrawalRecord) => (
        <div>
          <div>¥{record.withdrawalAmount.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: '#999' }}>净额 ¥{record.paymentAmount.toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: '累计充值/提现',
      key: 'totals',
      width: 150,
      render: (_: any, record: WithdrawalRecord) => (
        <div>
          <div>充 ¥{record.accumulatedDeposit.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: '#999' }}>提 ¥{record.accumulatedWithdrawal.toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: '银行账户',
      key: 'bank',
      width: 190,
      render: (_: any, record: WithdrawalRecord) => (
        <div>
          <div>{record.bankName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.withdrawalAccount}</div>
        </div>
      ),
    },
    {
      title: '申请时间',
      dataIndex: 'applyTime',
      key: 'applyTime',
      width: 160,
    },
    {
      title: '审核时间',
      dataIndex: 'auditTime',
      key: 'auditTime',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: '完成时间',
      dataIndex: 'completeTime',
      key: 'completeTime',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: '下发渠道',
      dataIndex: 'payoutChannel',
      key: 'payoutChannel',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: '状态/代付',
      key: 'status',
      width: 170,
      render: (_: any, record: WithdrawalRecord) => (
        <div>
          <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
          {record.payoutStatus && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{record.payoutStatus}</div>
          )}
          {record.auditReason && (
            <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 4 }}>
              {record.auditReason}
            </div>
          )}
          {record.rejectReason && (
            <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 4 }}>
              驳回: {record.rejectReason}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: WithdrawalRecord) => {
        if (record.status === WithdrawalStatus.PENDING_AUDIT) {
          return (
            <PermissionButton
              user={currentUser}
              page="withdrawal-risk-control"
              action="approve"
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => openAuditModal(record)}
              tooltip={hasPermission(currentUser, 'withdrawal-risk-control', 'approve')
                ? undefined
                : '您没有权限执行此操作'}
            >
              审核
            </PermissionButton>
          );
        }
        if (record.status === WithdrawalStatus.PENDING_PAYOUT || record.payoutStatus === '代付失败') {
          return (
            <PermissionButton
              user={currentUser}
              page="withdrawal-risk-control"
              action="approve"
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => openPayoutModal(record)}
              tooltip={hasPermission(currentUser, 'withdrawal-risk-control', 'approve')
                ? undefined
                : '您没有权限执行此操作'}
            >
              {record.payoutStatus === '代付失败' ? '重新出款' : '出款'}
            </PermissionButton>
          );
        }
        return '-';
      },
    },
  ];

  // 过滤数据
  const filteredData = withdrawalData.filter(item => {
    const matchesOrderNo = !orderNo || item.orderNo.includes(orderNo);
    const matchesUserId =
      !userId ||
      item.userId.includes(userId) ||
      item.uid.includes(userId) ||
      item.username.includes(userId);
    const matchesMemberAccount = !memberAccount || item.memberAccount.includes(memberAccount);
    const matchesStatus = auditStatus === '全部' || item.status === auditStatus;
    const matchesPayoutStatus = payoutStatus === '全部' || item.payoutStatus === payoutStatus;
    const matchesPayoutChannel = payoutChannel === '全部' || item.payoutChannel === payoutChannel;
    const timeValue =
      timeFilterType === 'apply'
        ? item.applyTime
        : timeFilterType === 'audit'
        ? item.auditTime
        : item.completeTime;
    const matchesTime = !timeRange || (() => {
      if (!timeValue) return false;
      const start = dayjs(timeRange[0]).startOf('day').valueOf();
      const end = dayjs(timeRange[1]).endOf('day').valueOf();
      const current = dayjs(timeValue).valueOf();
      return current >= start && current <= end;
    })();
    
    return (
      matchesOrderNo &&
      matchesUserId &&
      matchesMemberAccount &&
      matchesStatus &&
      matchesPayoutStatus &&
      matchesPayoutChannel &&
      matchesTime
    );
  });

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>提现风控与财务下款</span>
            <Space>
              <Button
                onClick={() => setBatchPayoutModalVisible(true)}
                disabled={!selectedRowKeys.length}
              >
                批量下款
              </Button>
              <Button
                type={autoPayoutEnabled ? 'default' : 'primary'}
                onClick={autoPayoutEnabled ? handleDisableAutoPayout : openAutoPayoutModal}
              >
                自动下款：{autoPayoutEnabled ? '已开启' : '已关闭'}
              </Button>
              <PermissionButton
                user={currentUser}
                page="withdraw:auto_audit:config"
                action="edit"
                type="primary"
                icon={<SettingOutlined />}
                onClick={autoAuditEnabled ? handleDisableAutoAudit : handleOpenConfig}
                style={{
                  backgroundColor: autoAuditEnabled ? '#52c41a' : '#1890ff',
                  borderColor: autoAuditEnabled ? '#52c41a' : '#1890ff',
                }}
                tooltip={hasPermission(currentUser, 'withdraw:auto_audit:config', 'edit') 
                  ? undefined 
                  : '您没有权限配置自动审核'}
              >
                自动审核：{autoAuditEnabled ? '已开启' : '已关闭'}
              </PermissionButton>
            </Space>
          </div>
        }
      >
        {/* 筛选区域 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="请输入提现订单号"
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="请输入会员账号"
                value={memberAccount}
                onChange={(e) => setMemberAccount(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="请输入会员ID/UID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Select
                value={auditStatus}
                onChange={setAuditStatus}
                style={{ width: '100%' }}
              >
                <Option value="全部">全部</Option>
                <Option value={WithdrawalStatus.PENDING_AUDIT}>待审核</Option>
                <Option value={WithdrawalStatus.PENDING_PAYOUT}>待出款</Option>
                <Option value={WithdrawalStatus.PAYOUT_PROCESSING}>出款中</Option>
                <Option value={WithdrawalStatus.REJECTED}>已驳回</Option>
                <Option value={WithdrawalStatus.PAID}>已出款</Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={6}>
              <Select value={payoutStatus} onChange={setPayoutStatus} style={{ width: '100%' }}>
                <Option value="全部">全部代付状态</Option>
                <Option value="代付中">代付中</Option>
                <Option value="代付成功">代付成功</Option>
                <Option value="代付失败">代付失败</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Select value={payoutChannel} onChange={setPayoutChannel} style={{ width: '100%' }}>
                <Option value="全部">全部出款渠道</Option>
                {PAYOUT_CHANNEL_OPTIONS.map(channel => (
                  <Option key={channel} value={channel}>
                    {channel}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select value={timeFilterType} onChange={setTimeFilterType} style={{ width: '100%' }}>
                <Option value="apply">申请时间</Option>
                <Option value="audit">审核时间</Option>
                <Option value="complete">完成时间</Option>
              </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                value={timeRange}
                onChange={setTimeRange}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col span={2}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1800 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record: WithdrawalRecord) => ({
              disabled: record.status !== WithdrawalStatus.PENDING_PAYOUT,
            }),
          }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          locale={{
            emptyText: '暂无数据',
          }}
        />
      </Card>

      {/* 批量下款弹窗 */}
      <Modal
        title="批量下款"
        open={batchPayoutModalVisible}
        onCancel={() => setBatchPayoutModalVisible(false)}
        onOk={handleBatchPayout}
        okText="确认下款"
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="仅可选择已审核且待出款订单"
          />
          <div>已选订单：{selectedRowKeys.length} 笔</div>
          <Select
            value={batchChannel}
            onChange={setBatchChannel}
            style={{ width: '100%' }}
          >
            {PAYOUT_CHANNEL_OPTIONS.map(channel => (
              <Option key={channel} value={channel}>
                {channel}
              </Option>
            ))}
          </Select>
        </Space>
      </Modal>

      {/* 自动下款配置 */}
      <Modal
        title="自动下款配置"
        open={autoPayoutModalVisible}
        onCancel={() => setAutoPayoutModalVisible(false)}
        onOk={handleSaveAutoPayout}
        okText="开启自动下款"
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="自动下款只针对“待出款”订单生效"
          />
          <Form layout="vertical">
            <Form.Item label="选择通道" required>
              <Select
                mode="multiple"
                value={autoPayoutChannels}
                onChange={setAutoPayoutChannels}
                placeholder="请选择自动下款通道"
              >
                {PAYOUT_CHANNEL_OPTIONS.map(channel => (
                  <Option key={channel} value={channel}>
                    {channel}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {autoPayoutChannels.length >= 2 && (
              <Form.Item label="通道选择规则" required>
                <Radio.Group value={autoPayoutRule} onChange={(e) => setAutoPayoutRule(e.target.value)}>
                  <Radio value="轮询">轮询</Radio>
                  <Radio value="随机">随机</Radio>
                </Radio.Group>
              </Form.Item>
            )}
            <Form.Item label="自动下款最大金额" required>
              <InputNumber
                min={1}
                value={autoPayoutMaxAmount}
                onChange={(value) => setAutoPayoutMaxAmount(value ?? undefined)}
                style={{ width: '100%' }}
                placeholder="低于该金额自动下款"
              />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 风控审核弹窗 */}
      <Modal
        title="风控审核"
        open={auditModalVisible}
        onCancel={() => {
          setAuditModalVisible(false);
          setSelectedRecord(null);
        }}
        width={980}
        footer={[
          <Button key="cancel" onClick={() => setAuditModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="reject"
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => {
              if (!selectedRecord) return;
              setRejectingRecord(selectedRecord);
              setRejectReason('');
              setRejectModalVisible(true);
              setAuditModalVisible(false);
            }}
          >
            驳回提现
          </Button>,
          <Button key="approve" type="primary" icon={<CheckCircleOutlined />} onClick={handleApprove}>
            通过审核
          </Button>,
        ]}
      >
        {selectedRecord && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small" title="核心指标">
              <Row gutter={16}>
                <Col span={6}>
                  <div>本次提现金额</div>
                  <strong>¥{formatCurrency(selectedRecord.withdrawalAmount)}</strong>
                </Col>
                <Col span={6}>
                  <div>累计存提差</div>
                  <strong>¥{formatCurrency(selectedRecord.ltv.net)}</strong>
                </Col>
                <Col span={6}>
                  <div>提充比</div>
                  <strong>
                    {selectedRecord.ltv.totalDeposit
                      ? (selectedRecord.ltv.totalWithdrawal / selectedRecord.ltv.totalDeposit).toFixed(2)
                      : '0.00'}
                  </strong>
                </Col>
                <Col span={6}>
                  <div>历史输赢</div>
                  <strong>¥{formatCurrency(selectedRecord.ltv.totalWinLoss)}</strong>
                </Col>
              </Row>
            </Card>

            <Card size="small" title="财务三维度分析">
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>生命周期价值 (LTV)</div>
                  <div>总存：¥{formatCurrency(selectedRecord.ltv.totalDeposit)}</div>
                  <div>总提：¥{formatCurrency(selectedRecord.ltv.totalWithdrawal)}</div>
                  <div>存提差：¥{formatCurrency(selectedRecord.ltv.net)}</div>
                </Col>
                <Col span={8}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>福利审计</div>
                  <div>总彩金：¥{formatCurrency(selectedRecord.welfare.totalBonus)}</div>
                  <div>总返水：¥{formatCurrency(selectedRecord.welfare.totalRebate)}</div>
                  <div>打码倍数：{selectedRecord.welfare.wageringMultiple.toFixed(2)}x</div>
                </Col>
                <Col span={8}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>本次打码</div>
                  <div>本次打码：¥{formatCurrency(selectedRecord.welfare.currentWagering)}</div>
                  <div>本次彩金：¥{formatCurrency(selectedRecord.welfare.currentBonus)}</div>
                  <div>本次倍数：{selectedRecord.welfare.currentWageringMultiple.toFixed(2)}x</div>
                </Col>
              </Row>
            </Card>

            <Card size="small" title="单笔存提分析">
              <Row gutter={16}>
                <Col span={8}>
                  <div>上笔充值金额</div>
                  <strong>¥{formatCurrency(selectedRecord.singleDeposit.lastDepositAmount)}</strong>
                  <div style={{ fontSize: 12, color: '#999' }}>{selectedRecord.singleDeposit.lastDepositTime}</div>
                </Col>
                <Col span={8}>
                  <div>上笔充值后提现次数</div>
                  <strong>{selectedRecord.singleDeposit.withdrawCountSinceLastDeposit}</strong>
                </Col>
                <Col span={8}>
                  <div>上笔充值后输赢</div>
                  <strong>¥{formatCurrency(selectedRecord.singleDeposit.winLossSinceLastDeposit)}</strong>
                </Col>
              </Row>
            </Card>

            <Card size="small" title="风控侦测">
              <Row gutter={16}>
                <Col span={12}>
                  <div>同设备关联（仅APP）</div>
                  <Typography.Text
                    style={{ fontSize: 18 }}
                    underline
                    onClick={() => openRelatedModal('同设备关联账号', selectedRecord.riskLinks.sameDeviceAccounts)}
                  >
                    {selectedRecord.riskLinks.sameDeviceCount}
                  </Typography.Text>
                </Col>
                <Col span={12}>
                  <div>近一月同IP个数</div>
                  <Typography.Text
                    style={{ fontSize: 18 }}
                    underline
                    onClick={() => openRelatedModal('近一月同IP账号', selectedRecord.riskLinks.sameIpAccounts)}
                  >
                    {selectedRecord.riskLinks.sameIpCount}
                  </Typography.Text>
                </Col>
              </Row>
            </Card>
          </Space>
        )}
      </Modal>

      {/* 关联账号弹窗 */}
      <Modal
        title={relatedModalTitle}
        open={relatedModalVisible}
        onCancel={() => setRelatedModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRelatedModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={520}
      >
        <Table
          rowKey="uid"
          dataSource={relatedAccounts}
          pagination={false}
          columns={[
            { title: '关联UID', dataIndex: 'uid', key: 'uid' },
            {
              title: '存提差',
              dataIndex: 'diff',
              key: 'diff',
              render: (value: number) => `¥${formatCurrency(value)}`,
            },
          ]}
        />
      </Modal>

      {/* 选择下发渠道 */}
      <Modal
        title="选择下发渠道"
        open={payoutModalVisible}
        onCancel={() => setPayoutModalVisible(false)}
        onOk={handleConfirmPayout}
        okText="确认下发"
        width={520}
      >
        {payoutRecord && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small" style={{ background: '#f5f7ff' }}>
              <div>待出款金额</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>¥{formatCurrency(payoutRecord.paymentAmount)}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                收款人：{payoutRecord.name} / {payoutRecord.bankName}
              </div>
            </Card>
            <Radio.Group
              onChange={(e) => setSelectedChannel(e.target.value)}
              value={selectedChannel}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value="渠道A（极速代付）">渠道A（极速代付）</Radio>
                <Radio value="渠道B（三方代付）">渠道B（三方代付）</Radio>
                <Radio value="渠道C（网银出款）">渠道C（网银出款）</Radio>
              </Space>
            </Radio.Group>
          </Space>
        )}
      </Modal>

      {/* 自动审核配置弹窗 */}
      <Modal
        title="自动审核配置"
        open={configModalVisible}
        onCancel={handleCloseConfig}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCloseConfig}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveConfig}>
            确认并启用
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {/* 打码量检测 */}
          <Form.Item name="checkWagering" valuePropName="checked">
            <Checkbox>是否检测打码量</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkWagering !== curr.checkWagering}>
            {({ getFieldValue }) =>
              getFieldValue('checkWagering') && (
                <Alert
                  message='选择"是"，当前打码需求不为0，需要人工审核'
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>

          {/* 新用户检测 */}
          <Form.Item name="checkNewUser" valuePropName="checked">
            <Checkbox>是否检测新用户</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkNewUser !== curr.checkNewUser}>
            {({ getFieldValue }) =>
              getFieldValue('checkNewUser') && (
                <Alert
                  message='选择"是"，用户首次提现需要人工审核'
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>

          {/* 大额金额定义 */}
          <Form.Item name="checkLargeAmount" valuePropName="checked">
            <Checkbox>大额金额定义</Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.checkLargeAmount !== curr.checkLargeAmount}
          >
            {({ getFieldValue }) =>
              getFieldValue('checkLargeAmount') && (
                <Form.Item
                  name="largeAmountThreshold"
                  label="金额阈值（元）"
                  rules={[{ required: true, message: '请输入金额阈值' }]}
                  style={{ marginLeft: 24 }}
                >
                  <InputNumber
                    min={0}
                    placeholder="提现金额大于此值需要人工审核"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkLargeAmount !== curr.checkLargeAmount}>
            {({ getFieldValue }) =>
              getFieldValue('checkLargeAmount') && (
                <Alert
                  message="提现金额大于配置金额，需要人工审核。不填写则不判断"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>

          {/* 提现次数定义 */}
          <Form.Item name="checkWithdrawalCount" valuePropName="checked">
            <Checkbox>提现次数定义</Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.checkWithdrawalCount !== curr.checkWithdrawalCount}
          >
            {({ getFieldValue }) =>
              getFieldValue('checkWithdrawalCount') && (
                <Form.Item
                  name="withdrawalCountThreshold"
                  label="次数阈值"
                  rules={[{ required: true, message: '请输入次数阈值' }]}
                  style={{ marginLeft: 24 }}
                >
                  <InputNumber
                    min={0}
                    placeholder="提现次数大于此值需要人工审核"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkWithdrawalCount !== curr.checkWithdrawalCount}>
            {({ getFieldValue }) =>
              getFieldValue('checkWithdrawalCount') && (
                <Alert
                  message="提现次数大于配置次数，需要人工审核。不填写则不判断"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>

          {/* 提充比定义 */}
          <Form.Item name="checkDepositWithdrawalRatio" valuePropName="checked">
            <Checkbox>提充比定义</Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.checkDepositWithdrawalRatio !== curr.checkDepositWithdrawalRatio}
          >
            {({ getFieldValue }) =>
              getFieldValue('checkDepositWithdrawalRatio') && (
                <Form.Item
                  name="depositWithdrawalRatioThreshold"
                  label="提充比阈值"
                  rules={[{ required: true, message: '请输入提充比阈值' }]}
                  style={{ marginLeft: 24 }}
                >
                  <InputNumber
                    min={0}
                    step={0.1}
                    placeholder="提充比大于此值需要人工审核"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkDepositWithdrawalRatio !== curr.checkDepositWithdrawalRatio}>
            {({ getFieldValue }) =>
              getFieldValue('checkDepositWithdrawalRatio') && (
                <Alert
                  message="提充比大于配置值，则需要人工审核。不填写则不判断"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>

          {/* 异常标签ID */}
          <Form.Item name="checkAbnormalTag" valuePropName="checked">
            <Checkbox>异常标签ID</Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.checkAbnormalTag !== curr.checkAbnormalTag}
          >
            {({ getFieldValue }) =>
              getFieldValue('checkAbnormalTag') && (
                <Form.Item
                  name="abnormalTagIds"
                  label="标签ID（多个用逗号分隔）"
                  rules={[{ required: true, message: '请输入异常标签ID' }]}
                  style={{ marginLeft: 24 }}
                >
                  <Input
                    placeholder="带上填写标签的用户的提现，需要人工审核"
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.checkAbnormalTag !== curr.checkAbnormalTag}>
            {({ getFieldValue }) =>
              getFieldValue('checkAbnormalTag') && (
                <Alert
                  message="带上填写标签的用户的提现，需要人工审核。不填写则不判断"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, marginLeft: 24 }}
                />
              )
            }
          </Form.Item>
          </Form>
      </Modal>

      {/* 驳回原因输入弹窗 */}
      <Modal
        title="驳回提现申请"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectingRecord(null);
          setRejectReason('');
        }}
        onOk={handleConfirmReject}
        okText="确认驳回"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>订单号：{rejectingRecord?.orderNo}</div>
          <div>提现金额：¥{rejectingRecord?.withdrawalAmount.toFixed(2)}</div>
        </div>
        <Input.TextArea
          placeholder="请输入驳回原因（必填）"
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={200}
          showCount
        />
      </Modal>
    </div>
  );
};

export default WithdrawalRiskControl;

